import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';

import useTypedSWR from 'src/components/general/useTypedSWR';
import { VISIT_AUTO_CANCEL_INFO_QUERY } from 'src/components/pages/pageElements/videoConference/useSWRVisitAutoCancelInfo.gql';

export const useSWRVisitAutoCancelInfo = (
  eventId: string | undefined,
  options?: { pollingInterval?: number },
) => {
  const { pollingInterval = 5000 } = options || {};

  const [isAfterAutoCancelAt, setIsAfterAutoCancelAt] = useState(false);

  const { data, error, mutate } = useTypedSWR(
    eventId ? [VISIT_AUTO_CANCEL_INFO_QUERY, { eventId }] : null,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      refreshInterval: latestData =>
        isAfterAutoCancelAt &&
        !latestData?.staff_getVisitAutoCancelInfo?.autoCanceledAt &&
        !latestData?.staff_getVisitAutoCancelInfo?.isOverridden &&
        !latestData?.staff_getVisitAutoCancelInfo?.sessionOverlappedAt
          ? pollingInterval
          : 0,
    },
  );

  const autoCancelAt = data?.staff_getVisitAutoCancelInfo.autoCancelAt
    ? parseISO(data?.staff_getVisitAutoCancelInfo.autoCancelAt)
    : null;

  // Set timer that will trigger a state update when the auto-cancel time is reached.
  // The state update (isAfterAutoCancelAt) is used to start polling for updated info.
  useEffect(() => {
    // No autoCancelAt set. Either not fetched yet or no auto-cancel for this visit
    if (!autoCancelAt) {
      setIsAfterAutoCancelAt(false);
      return undefined; // No timeout to clear so we don't need a cleanup function
    }

    const timeUntilAutoCancel = autoCancelAt.getTime() - new Date().getTime();

    // Already past the auto-cancel time
    if (timeUntilAutoCancel <= 0) {
      setIsAfterAutoCancelAt(true);
      return undefined; // No timeout to clear so we don't need a cleanup function
    }

    // Set timeout to trigger when auto-cancel time is reached
    const timeout = setTimeout(() => {
      setIsAfterAutoCancelAt(true);
    }, timeUntilAutoCancel);

    // Cleanup function
    return () => {
      clearTimeout(timeout);
    };
  }, [autoCancelAt]);

  return {
    data: {
      autoCancelAt,
      autoCanceledAt: data?.staff_getVisitAutoCancelInfo.autoCanceledAt
        ? parseISO(data?.staff_getVisitAutoCancelInfo.autoCanceledAt)
        : null,
      isOverridden: !!data?.staff_getVisitAutoCancelInfo.isOverridden,
      isAutoCancelPrevented: !!data?.staff_getVisitAutoCancelInfo.isAutoCancelPrevented,
      hasSessionOverlapped: !!data?.staff_getVisitAutoCancelInfo.sessionOverlappedAt,
    },
    error,
    isLoading: !data && !error && !!eventId,
    mutate,
  };
};
